import { useMemo } from 'react';
import { evaluateCapacity } from '../logic/capacityEngine.js';
import { buildSchedulingSuggestions } from '../logic/schedulingSuggestions.js';
import { buildPlannerSuggestions as buildCapacitySuggestions } from '../logic/suggestionEngine.js';

const toDate = (value) => {
  const dt = value ? new Date(value) : null;
  return Number.isFinite(dt?.getTime?.()) ? dt : null;
};

export function usePlannerSuggestions({ tasks = [], capacity = null } = {}) {
  const suggestions = useMemo(() => {
    const out = [];
    const now = Date.now();
    const safeTasks = Array.isArray(tasks) ? tasks : [];
    const cap =
      capacity && (capacity.dryingWarnings || capacity.kilnWarnings || capacity.dailyLoadChart)
        ? capacity
        : evaluateCapacity(capacity || {});

    if (!safeTasks.length) {
      out.push({
        id: 'seed-plan',
        title: 'Generate a starter plan',
        message: 'Use "Generate Production Plan" to create tasks from your products.',
      });
    }

    const overdue = safeTasks.filter((t) => {
      const end = toDate(t.end || t.dueDate || t.deadline);
      return end && end.getTime() < now;
    });
    if (overdue.length) {
      out.push({
        id: 'overdue',
        title: `${overdue.length} overdue task${overdue.length === 1 ? '' : 's'}`,
        message: `Prioritize: ${overdue.slice(0, 2).map((t) => t.title || t.id).join(', ')}`,
      });
    }

    const missingDates = safeTasks.filter((t) => !t.end && !t.dueDate && !t.deadline);
    if (missingDates.length) {
      out.push({
        id: 'missing-dates',
        title: 'Add due dates',
        message: `Set due dates for ${missingDates.slice(0, 3).map((t) => t.title || t.id).join(', ')}`,
      });
    }

    const { utilizationPct } = cap || {};
    if (utilizationPct >= 0.9) {
      out.push({
        id: 'capacity-high',
        title: 'Load is high',
        message: 'Consider staggering firings or pausing new intake.',
      });
    }

    // Intelligent scheduling suggestions (read-only)
    const scheduling = buildSchedulingSuggestions({ tasks: safeTasks, capacity: cap || {} });
    out.push(...scheduling);

    // Capacity-driven suggestions from suggestion engine v1
    const smart = buildCapacitySuggestions({ tasks: safeTasks, capacity: cap || {}, now: new Date() });
    out.push(...smart);

    return out;
  }, [tasks, capacity]);

  return { suggestions };
}
